
export const DashboardQueries = {
    "Canvas-Student Advising": "?Set%20Desired%20Average=60&Minimum%20Passing%20Grade=60",
    "Canvas-Student Advising BETA": "?Set%20Desired%20Average=60&Minimum%20Passing%20Grade=60",
    "Canvas-Student Course Performance": "?Set%20Desired%20Average=60&Minimum%20Passing%20Grade=60&Set%20Desired%20Submission%20Average=60",
    "Canvas-Student Course Performance BETA": "?Set%20Desired%20Average=60&Minimum%20Passing%20Grade=60&Set%20Desired%20Submission%20Average=60",
    "Assessment and Item Effectiveness": "?Desired%20Final%20Average=60&Minimum%20Passing%20Grade=60",
    "Assessment And Item Effectiveness": "?Desired%20Final%20Average=60&Minimum%20Passing%20Grade=60",
    "Curriculum Effectiveness and Gap Analysis": "?Student%20Performance%20Threshold=60&Minimum%20Passing%20Grade=60",
    "Student Academic Advising": "?Desired%20final%20average=60&Minimum%20Passing%20Grade=60",
    "Student Performance and Course Metrics": "?Desired%20final%20average=60&Minimum%20Passing%20Grade=60",
    "Students and Outcomes": "?At%20Risk%20Threshold=60&Minimum%20Passing%20Grade=60",
    "Students and Outcomes-Anonymized": "?At%20Risk%20Threshold=60&Minimum%20Passing%20Grade=60"
};

/**
 * Tableau *filters* (not parameters) that default to the school's active terms
 * (EAI-5645), keyed by dashboard name like `DashboardQueries` but matched
 * ignoring case ("Assessment and/And Item Effectiveness" both exist). Parameters
 * ride in the viz URL; these are multi-select filters, so they are applied with
 * `applyFilterAsync` once the viz is interactive.
 */
export const DashboardActiveTermFilters: Record<string, string[]> = {
    "Canvas Outcomes": ["Term Filter"],
    "Canvas Outcomes With Defined Groups": ["Term Filter"],
    "Canvas-Student Advising": ["Term Filter"],
    "Canvas-Student Course Performance": ["Term Filter"],
    "Blackboard-Student Advising": ["Term Filter"],
    "Blackboard-Student Course Performance": ["Term Filter"],
    "Assessment and Item Effectiveness": ["Term Filter"],
    "Required Categories for Items": ["Term Filter"],
    "Student Academic Advising": ["Term Filter"],
    "Student Performance and Course Metrics": ["Term Filter"],
    "Students and Outcomes": ["Term Filter"],
    "Students and Outcomes-Anonymized": ["Term Filter"],
};

const activeTermFiltersByName = new Map(
    Object.entries(DashboardActiveTermFilters).map(([name, fields]) => [name.toLowerCase(), fields])
);

interface ActiveTermLike {
    term_order: number,
    name: string,
}

/** A term as the workbooks label it, e.g. `251 | Winter 2025`. */
export const toTermFilterValue = (term: ActiveTermLike) => `${term.term_order} | ${term.name}`;

const normalizeFilterValue = (value: string) => value.trim().replace(/\s+/g, " ").toLowerCase();

/**
 * The wanted values that the filter actually offers, spelled the way Tableau
 * has them, since `applyFilterAsync` needs the domain's exact value. Matching
 * ignores case and extra whitespace. An empty result means nothing matched and
 * no filter should be applied.
 */
export function matchAvailableFilterValues(wanted: string[], available: string[]): string[] {
    const availableByKey = new Map(available.map(value => [normalizeFilterValue(value), value]));
    const matches: string[] = [];
    for (const value of wanted) {
        const match = availableByKey.get(normalizeFilterValue(value));
        if (match !== undefined && !matches.includes(match)) {
            matches.push(match);
        }
    }
    return matches;
}

/**
 * The filter values to seed on a dashboard's first load, as field name → values.
 * A field the URL already sets is left to the URL, and no active terms means no
 * defaults, so the workbook keeps its own.
 */
export function getActiveTermFilterDefaults(
    dashboardName: string | undefined,
    activeTerms: ActiveTermLike[] | undefined,
    queryParameters: Record<string, unknown> | undefined
): Record<string, string[]> {
    const fields = dashboardName ? activeTermFiltersByName.get(dashboardName.toLowerCase()) : undefined;
    if (!fields || !activeTerms || activeTerms.length === 0) return {};

    const urlKeys = Object.keys(queryParameters || {}).map(key => key.toLowerCase());
    const values = activeTerms.map(toTermFilterValue);
    const defaults: Record<string, string[]> = {};
    for (const field of fields) {
        if (!urlKeys.includes(field.toLowerCase())) {
            defaults[field] = values;
        }
    }
    return defaults;
}